<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">{{ routeLabel }}</h3>
        <div class="text-xs-right d-flex align-center">
          <v-btn color="blue darken-3" dark small class="mr-2" @click="handleExport" :loading="exporting">
            <v-icon left small>download</v-icon>
            Export
          </v-btn>
          <refresh-button :click-function="fetchData" />
        </div>
      </v-flex>

      <v-flex xs12>
        <v-card>
          <v-layout class="py-2 px-3" row wrap align-center>
            <v-flex xs12 sm4 class="pr-2">
              <v-flex class="d-flex align-center">
                <custom-date-range-picker @change="fetchData"
                                          v-model="filters.date_range"></custom-date-range-picker>
              </v-flex>
            </v-flex>

            <v-flex xs3>
              <v-autocomplete
                  :items="countries"
                  chips
                  clearable
                  deletable-chips
                  flat
                  hide-details
                  item-text="country_name"
                  item-value="id"
                  label="Country"
                  multiple
                  prepend-inner-icon="filter_list"
                  solo
                  v-model="filters.country"
                  @change="fetchData"
              ></v-autocomplete>
            </v-flex>

            <v-flex xs3>
              <v-autocomplete
                  :items="visa_types"
                  @change="fetchData"
                  chips
                  clearable
                  deletable-chips
                  flat
                  hide-details
                  item-text="type"
                  item-value="id"
                  label="Visa Type"
                  multiple
                  prepend-inner-icon="filter_list"
                  solo
                  v-model="filters.visa_type"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs3>
              <v-autocomplete
                  :items="revenueTypes"
                  @change="fetchData"
                  clearable
                  deletable-chips
                  flat
                  hide-details
                  item-text="type"
                  item-value="id"
                  label="Revenue Type"
                  prepend-inner-icon="filter_list"
                  solo
                  v-model="filters.revenue_type"
              ></v-autocomplete>
            </v-flex>
          </v-layout>

          <v-divider />

          <v-card-text class="pa-0">
            <v-data-table :headers="headers" :items="rows" :loading="loading" :options.sync="pagination"
                          :server-items-length="serverTotal" :footer-props="footerProps" item-key="id">
              <template v-slot:item="{ item, index }">
                <tr >
                  <td>{{ (pagination.page - 1) * pagination.itemsPerPage + index + 1 }}</td>
                  <td style="cursor: pointer">{{ item.country_name }}</td>
                  <td>{{ item.visa_type_name }}</td>
                  <td>{{ item.revenue_type }}</td>
                  <td class="text-right">{{ item.visa_fee_usd }}</td>
                  <td class="text-right">{{ item.late_fee_usd }}</td>
                  <td class="text-right">{{ item.penalty_npr }}</td>
                  <td class="text-right">{{ item.amount_usd }}</td>
                  <td class="text-right">{{ item.amount_npr }}</td>
                  <td>{{ item.date_for }}</td>
                </tr>
              </template>

              <template v-slot:body.append>
                <tr class="grey lighten-2 font-weight-bold">
                  <td>-</td>
                  <td>Total</td>
                  <td>-</td>
                  <td>-</td>
                  <td v-for="field in totalFields" :key="field" class="text-right">{{ totals[field] }}</td>
                  <td>-</td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="exportDialog" persistent width="500">
      <v-card>
        <v-toolbar color="blue darken-3" dark>
          <v-toolbar-title>Export Options</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="exportDialog = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-form ref="exportFormRef" v-model="exportValid" lazy-validation>
            <v-text-field v-model="exportName" label="Name" outlined />
            <v-text-field v-model="exportRows" :rules="[rules.required]" label="Number of rows to export *" type="number" outlined />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="red lighten-1" class="white--text" @click="exportDialog = false">Cancel</v-btn>
          <v-btn color="blue darken-3" class="white--text" :loading="exporting" @click="handleExportClicked">Export</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Http from "axios";
import { VERSIONED_API_URL } from "../../config/config";
import RefreshButton from "@/components/RefreshButton";
import customDateRangePicker from "../../components/CustomDateRangePicker.vue";
export default {
  name: "SummaryReportRevenueCountry",
  components: { RefreshButton,customDateRangePicker },

  data() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayDate = yesterday.toISOString().split("T")[0];

    return {
      rows: [], loading: false, serverTotal: 0,
      filters: { date_range: { start: yesterdayDate, end: yesterdayDate }, country: [] },
      dateMenu: false, dateRangeValues: [yesterdayDate, yesterdayDate],
      pagination: { page: 1, itemsPerPage: 25, sortBy: ["created_at"], sortDesc: [true] },
      footerProps: { "items-per-page-options": [10, 25, 50, 100,{text: 'All', 'value': -1}] },
      exportDialog: false, exporting: false, exportValid: true, exportName: "", exportRows: 500,
      rules: { required: (v) => !!v || "This field is required" },
      totalFields: ["visa_fee_usd", "late_fee_usd", "penalty_npr", "amount_usd", "amount_npr"],
      revenueTypes:[
          'Visa Fee','Late Stay Fee','Conversion Fee','Transfer Fee','Penalty','Extension Fee'
      ],
      headers: [
        { text: "S.N.",           value: "s_no",            sortable: false },
        { text: "Country",        value: "country_name",  sortable: false },
        { text: "Visa Type",      value: "visa_type_name", sortable: false },
        { text: "Revenue Type",   value: "revenue_type",  sortable: false },
        { text: "Visa Fee (USD)", value: "visa_fee_usd",  sortable: false, align: "right" },
        { text: "Late Fee (USD)", value: "late_fee_usd",  sortable: false, align: "right" },
        { text: "Penalty (NPR)",  value: "penalty_npr",   sortable: false, align: "right" },
        { text: "Amount (USD)",   value: "amount_usd",    sortable: false, align: "right" },
        { text: "Amount (NPR)",   value: "amount_npr",    sortable: false, align: "right" },
        { text: "Date",           value: "date_for",      sortable: false },
      ],
    };
  },

  computed: {
    ...mapGetters({ countries: "CountryList/all" , visa_types: "VisaTypeList/all"}),
    routeLabel() { return this.$route.meta.label || "Revenue Summary by Country"; },
    formattedDateRange() {
      const { start, end } = this.filters.date_range || {};
      if (start && end) return `${start} ~ ${end}`;
      return start || "";
    },
    totals() {
      const result = {};
      this.totalFields.forEach((f) => (result[f] = 0));
      this.rows.forEach((row) => this.totalFields.forEach((f) => { result[f] += parseFloat(row[f]) || 0; }));
      Object.keys(result).forEach((k) => { result[k] = result[k].toFixed(2); });
      return result;
    },
  },

  watch: {
    pagination: { deep: true, handler() { this.fetchData(); } },
  },

  methods: {
    ...mapActions({ fetchCountries: "CountryList/fetchCountries" }),

    buildParams() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      const { start, end } = this.filters.date_range || {};
      const params = { page, per_page: itemsPerPage, sort_by: sortBy[0] || "created_at", descending: sortDesc[0] || true, paginate: true };
      if (start) params.start_date = start;
      if (end)   params.end_date   = end;
      if (this.filters.country.length) params.country = this.filters.country;
      if ((this.filters.visa_type && this.filters.visa_type.length)) params.visa_type = this.filters.visa_type;
      if(this.filters.revenue_type) params.revenue_type = this.filters.revenue_type;
      return params;
    },

    fetchData() {
      this.loading = true;
      Http.get(`${VERSIONED_API_URL}/summary-report-data/revenue-country`, { params: this.buildParams() })
          .then((res) => { this.rows = res.data.data || res.data; this.serverTotal = res.data.meta.total || this.rows.length; })
          .catch((err) => console.error(err))
          .finally(() => { this.loading = false; });
    },

    onDateRangeChange(val) {
      if (val.length === 2) {
        const [a, b] = val.slice().sort();
        this.filters.date_range = { start: a, end: b };
        this.dateMenu = false;
        this.fetchData();
      }
    },

    handleExport() { this.exportDialog = true; },

    handleExportClicked() {
      if (!this.$refs.exportFormRef.validate()) return;
      this.exporting = true;
      Http.post(`${VERSIONED_API_URL}/summary-report-data/revenue-country/export`,
          { ...this.buildParams(), exportName: this.exportName, exportRows: this.exportRows },)
          .then((res) => {
            window.open(res.data.url, "_blank");
            this.exportDialog = false;
          })
          .catch((err) => console.error(err))
          .finally(() => { this.exporting = false; });
    },
    goToRevenueReport(item) {
      const matchedCountry = this.countries.find(c => c.country_name === item.country_name);
      const matchedVisaType = this.visa_types.find(v => v.type === item.visa_type_name);

      const route = this.$router.resolve({
        name: 'RevenueReport',
        query: {
          start_date: item.date_for,
          end_date: item.date_for,
          country: matchedCountry ? matchedCountry.id : null,
          visa_type: matchedVisaType ? matchedVisaType.id : null,
        }
      });
      window.open(route.href, '_blank');
    },
  },

  mounted() {
    if (this.$store.state.CountryList.all.length === 0) this.fetchCountries();
    this.$store.dispatch('VisaTypeList/fetchAll');
    this.fetchData();
  },
};
</script>

<style scoped>
.font-weight-bold td { font-weight: 700; }
</style>